import * as React from 'react';
import Alert from './alert';
import { AlertItem, AlertItemProps } from './alert-item';
import { useI18n } from '../i18n-provider';

export interface AlertContextState {
    showAlert: (
        title: string,
        content: React.ReactNode | string,
        props?: { id?: string } & Partial<AlertItemProps>,
    ) => string;
    showInfo: (content: React.ReactNode | string, props?: Partial<AlertItemProps>) => string;
    showWarning: (content: React.ReactNode | string, props?: Partial<AlertItemProps>) => string;
    showError: (content: React.ReactNode | string, props?: Partial<AlertItemProps>) => string;
    closeAlert: (key: string) => void;
}

export interface AlertProviderProps {
    itemOverrides?: {
        theme?: 'light' | 'dark';
        autoClose?: boolean;
        delay?: number;
        dismissible?: boolean;
        onClose?: (id?: string) => void;
    };
}

const ImperativeAlertContext = React.createContext<AlertContextState>(null);

export const AlertProvider: React.FunctionComponent<React.PropsWithChildren<AlertProviderProps>> = (props) => {
    const { itemOverrides, children } = props;
    const [alertItems, setAlertItems] = React.useState<React.PropsWithChildren<{ id?: string } & AlertItemProps>[]>([]);
    const messages = useI18n();
    const closeAlert: AlertContextState['closeAlert'] = (id) => {
        setAlertItems((oldAlertItems) =>
            oldAlertItems.map((item) => (item.id === id ? { ...item, open: false } : item)),
        );
        setTimeout(() => setAlertItems((oldAlertProps) => oldAlertProps.filter((item) => item.id !== id)), 500);
    };

    const showInfo: AlertContextState['showInfo'] = (content, props?: Partial<AlertItemProps>) => {
        return showAlert(messages['alert.information'], content, {
            autoClose: true,
            variant: 'info',
            dismissible: true,
            ...props,
        });
    };

    const showWarning: AlertContextState['showWarning'] = (content, props?: Partial<AlertItemProps>) => {
        return showAlert(messages['alert.warning'], content, {
            autoClose: true,
            variant: 'warning',
            dismissible: true,
            ...props,
        });
    };

    const showError: AlertContextState['showError'] = (content, props?: Partial<AlertItemProps>) => {
        return showAlert(messages['alert.Error'], content, {
            autoClose: true,
            variant: 'alert',
            dismissible: true,
            ...props,
        });
    };

    const showAlert: AlertContextState['showAlert'] = (title, content, props = {}) => {
        const { id, onClose, ...rest } = props;

        const myId = id || `${new Date().getTime() + Math.random()}`;
        setAlertItems((oldAlertItems) => [
            ...oldAlertItems,
            {
                title,
                children: content,
                id: myId,
                ...rest,
                inline: false,
                open: false,
                dark: props.dark ? props.dark : itemOverrides?.theme && itemOverrides?.theme === 'dark',
                autoClose: props.autoClose != undefined ? props.autoClose : itemOverrides?.autoClose,
                delay: props.delay ? props.delay : itemOverrides?.delay,
                dismissible: props.dismissible != undefined ? props.dismissible : itemOverrides?.dismissible,
                onClose: () => {
                    onClose && onClose();
                    itemOverrides?.onClose && itemOverrides.onClose();
                    closeAlert(myId);
                },
            },
        ]);

        setTimeout(() =>
            setAlertItems((oldAlertItems) =>
                oldAlertItems.map((item) => (item.id === myId ? { ...item, open: true } : item)),
            ),
        );

        return myId;
    };

    return (
        <ImperativeAlertContext.Provider value={{ showAlert, closeAlert, showInfo, showError, showWarning }}>
            {children}

            <Alert>
                {alertItems.map((props) => (
                    <AlertItem {...props} key={props.id} id={props.id}>
                        {props.children}
                    </AlertItem>
                ))}
            </Alert>
        </ImperativeAlertContext.Provider>
    );
};

export const useAlerts = (): AlertContextState => React.useContext(ImperativeAlertContext);
